import {CancellationPenalty} from '../types/offer'
import {getRefundType} from './cancellationPenalties'
import {getOfferService} from './meal'

interface OfferPerks {
  penaltyType?: number
  boardType?: number
}

/**
 * Generates a string representing the combination of refund type and service level for an offer.
 * @param cancellationPenalties - A list of cancellation penalties associated with the offer.
 * @param services - A list of services included with the offer (e.g., meal, snack).
 * @returns A string in the format `<refundType>|<service>` if both inputs are provided; otherwise, returns `null`.
 */
export const getOfferPerks = (
  cancellationPenalties: CancellationPenalty[] | undefined,
  services: string[] | undefined
) => {
  if (!cancellationPenalties || !services) return null
  const refundType = getRefundType(cancellationPenalties)
  const service = getOfferService(services)

  return `${refundType}|${service}`
}

/**
 * Parses a serialized offer perks string (formatted as `<penaltyType>|<boardType>`)
 * and returns an object containing numeric values for each part.
 * @param offerPerks - A string representing the offer perks, typically in the format `<penaltyType>|<boardType>`.
 * @returns An `OfferPerks` object containing `penaltyType` and `boardType` as numbers, or an empty object if input is missing or invalid.
 */
export const parseOfferPerks = (offerPerks?: string): OfferPerks => {
  if (!offerPerks) {
    return {}
  }

  const [firstPartStr, secondPartStr] = offerPerks.split('|')

  const penaltyType = Number(firstPartStr) ?? undefined
  const boardType = Number(secondPartStr) ?? undefined
  return {
    penaltyType,
    boardType
  }
}
